import {
  apiPresignClientUpload, apiConfirmClientUpload,
  apiPresignMilestoneUpload, apiConfirmMilestoneUpload,
  apiPresignSharedUpload, apiConfirmSharedUpload,
  apiPresignPrivateFile, apiConfirmPrivateFile,
  apiPresignPingAttachment, apiPresignPingLockerFile, apiConfirmPingLockerFile,
} from './api';

// Keep in sync with MAX_UPLOAD_BYTES in the backend (utils/r2.js).
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024; // 500 MB
export const MAX_UPLOAD_MB = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));

export const formatBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} B`;
  const units = ['KB', 'MB', 'GB'];
  let val = n / 1024, i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`;
};

/** PUT a file straight to R2 via XHR so we get real upload progress events. */
function putToUrl(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    // Must match the Content-Type the URL was signed with, or R2 rejects it.
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.timeout = 10 * 60 * 1000; // 10 minutes for large manuscripts
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Storage rejected the upload (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload — please try again'));
    xhr.ontimeout = () => reject(new Error('Upload timed out — please try again'));
    xhr.send(file);
  });
}

/**
 * presign → direct-to-R2 PUT (with progress) → confirm.
 * Returns the persisted file record. Throws a friendly Error on any failure.
 */
async function uploadToR2({ file, presign, confirm, onProgress }) {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`"${file.name}" is too large (max ${MAX_UPLOAD_MB} MB)`);
  }
  const contentType = file.type || 'application/octet-stream';
  const signed = await presign({ filename: file.name, contentType, size: file.size });
  await putToUrl(signed.url, file, signed.contentType || contentType, onProgress);
  return await confirm({ key: signed.key, name: file.name });
}

export const uploadClientFile = (token, file, { requestId = null, onProgress } = {}) =>
  uploadToR2({
    file,
    presign: (meta) => apiPresignClientUpload(token, meta),
    confirm: ({ key, name }) => apiConfirmClientUpload(token, { key, name, requestId }),
    onProgress,
  });

export const uploadMilestoneFile = (token, projectId, milestoneId, file, { onProgress } = {}) =>
  uploadToR2({
    file,
    presign: (meta) => apiPresignMilestoneUpload(token, projectId, milestoneId, meta),
    confirm: ({ key, name }) => apiConfirmMilestoneUpload(token, projectId, milestoneId, { key, name }),
    onProgress,
  });

export const uploadSharedFile = (token, projectId, file, { onProgress } = {}) =>
  uploadToR2({
    file,
    presign: (meta) => apiPresignSharedUpload(token, projectId, meta),
    confirm: ({ key, name }) => apiConfirmSharedUpload(token, projectId, { key, name }),
    onProgress,
  });

export const uploadPrivateFile = (token, folderId, file, { onProgress } = {}) =>
  uploadToR2({
    file,
    presign: (meta) => apiPresignPrivateFile(token, folderId, meta),
    confirm: ({ key, name }) => apiConfirmPrivateFile(token, folderId, { key, name }),
    onProgress,
  });

export const uploadPingAttachment = async (token, conversationId, file, { onProgress } = {}) => {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error(`"${file.name}" is too large (max ${MAX_UPLOAD_MB} MB)`);
  const contentType = file.type || 'application/octet-stream';
  const signed = await apiPresignPingAttachment(token, conversationId, { filename: file.name, contentType, size: file.size });
  await putToUrl(signed.url, file, signed.contentType || contentType, onProgress);
  return { storageKey: signed.key, name: file.name };
};

export const uploadPingLockerFile = (token, conversationId, file, { onProgress } = {}) =>
  uploadToR2({
    file,
    presign: (meta) => apiPresignPingLockerFile(token, conversationId, meta),
    confirm: ({ key, name }) => apiConfirmPingLockerFile(token, conversationId, { storageKey: key, name }),
    onProgress,
  });
